import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Calendar, Award, MapPin, Briefcase, 
  Eye, MessageSquare, BookOpen, Download, Share2, Star, Check,
  Clock, DollarSign, Users, Globe
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DigiTPro = () => {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  useEffect(() => {
    fetchTrainerProfile();
  }, [trainerId]);

  const fetchTrainerProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // If no trainerId in URL, show current user's profile
      const targetId = trainerId || currentUser.id;
      setIsOwnProfile(!trainerId || trainerId === currentUser.id);

      // Fetch trainer profile
      const response = await axios.get(`${API_BASE_URL}/api/users/${targetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTrainer(response.data);
    } catch (err) {
      console.error('Error fetching trainer profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = () => {
    navigate(`/messages/compose?to=${trainer.id}`);
  };

  const handleBook = () => {
    // Navigate to booking flow
    navigate(`/book-session/${trainer.id}`);
  };

  const handleDownload = () => {
    window.print();
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: `${trainer?.name}'s Digital Trainer Profile`,
        text: `Check out ${trainer?.name}'s professional trainer profile`,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-gray-600">Trainer profile not found</p>
            <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Main Profile Card */}
        <Card className="shadow-2xl border-2 border-blue-100">
          <CardContent className="p-8">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6 mb-8">
              <Avatar className="w-32 h-32 border-4 border-blue-200">
                <AvatarImage src={trainer.avatar} alt={trainer.name} />
                <AvatarFallback className="text-3xl bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                  {trainer.name?.charAt(0) || 'T'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{trainer.name}</h1>
                <p className="text-lg text-gray-600 mb-3">{trainer.title || 'Professional Trainer'}</p>
                
                {/* Rating */}
                <div className="flex items-center justify-center md:justify-start space-x-2 mb-4">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">(4.9 / 5.0 - 127 reviews)</span>
                </div>

                {/* Location and Experience */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-gray-600">
                  {trainer.location && (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {trainer.location}
                    </div>
                  )}
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-1" />
                    15+ years experience
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    500+ clients trained
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Certifications Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Award className="w-5 h-5 mr-2 text-blue-600" />
                Certifications & Credentials
              </h3>
              <div className="flex flex-wrap gap-2">
                <Badge className="px-4 py-2 bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300 text-sm">
                  <Check className="w-4 h-4 mr-1" />
                  ICF Certified
                </Badge>
                <Badge className="px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border-blue-300 text-sm">
                  <Check className="w-4 h-4 mr-1" />
                  PMP
                </Badge>
                <Badge className="px-4 py-2 bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300 text-sm">
                  <Check className="w-4 h-4 mr-1" />
                  Scrum Master
                </Badge>
                <Badge className="px-4 py-2 bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-300 text-sm">
                  <Check className="w-4 h-4 mr-1" />
                  Six Sigma Black Belt
                </Badge>
                <Badge variant="outline" className="px-4 py-2 text-sm">
                  +3 more
                </Badge>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Contact Information */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium text-blue-600">{trainer.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">+1 (555) 123-4567</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Globe className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Website</p>
                    <p className="font-medium text-purple-600">www.trainerwebsite.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Rate</p>
                    <p className="font-medium text-gray-900">$200-500 / session</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Session History */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg">
                  <Calendar className="w-6 h-6 text-gray-600" />
                  <div>
                    <p className="text-xs text-gray-500">Last Session</p>
                    <p className="font-semibold text-gray-900">9/25/2024</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 p-4 border-2 border-green-300 bg-green-50 rounded-lg">
                  <Calendar className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Next Available</p>
                    <p className="font-semibold text-green-600">10/2/2024</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Specializations */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Areas of Expertise</h3>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="px-3 py-1">Leadership Development</Badge>
                <Badge variant="outline" className="px-3 py-1">Team Building</Badge>
                <Badge variant="outline" className="px-3 py-1">Change Management</Badge>
                <Badge variant="outline" className="px-3 py-1">Agile Methodologies</Badge>
                <Badge variant="outline" className="px-3 py-1">Executive Coaching</Badge>
                <Badge variant="outline" className="px-3 py-1">Digital Transformation</Badge>
                <Badge variant="outline" className="px-3 py-1">Project Management</Badge>
                <Badge variant="outline" className="px-3 py-1">Strategic Planning</Badge>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline" 
                className="flex-1 sm:flex-none px-8"
                onClick={() => navigate(`/profile/${trainer.id}`)}
              >
                <Eye className="w-5 h-5 mr-2" />
                View Profile
              </Button>
              
              {!isOwnProfile && (
                <>
                  <Button 
                    variant="outline" 
                    className="flex-1 sm:flex-none px-8"
                    onClick={handleMessage}
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Message
                  </Button>
                  
                  <Button 
                    className="flex-1 sm:flex-none px-8 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                    onClick={handleBook}
                  >
                    <BookOpen className="w-5 h-5 mr-2" />
                    Book Session
                  </Button>
                </>
              )}
            </div>

            {/* Professional Summary */}
            <div className="mt-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional Summary</h3>
              <p className="text-gray-700 leading-relaxed">
                Award-winning corporate trainer with 15+ years of experience delivering high-impact training programs. 
                Specialized in leadership development, agile transformation, and executive coaching. Certified by ICF 
                and PMI, with a proven track record of training over 500 professionals across Fortune 500 companies. 
                Known for interactive, results-driven training approaches that combine theory with practical application.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-600">
          <p>This Digital Trainer Profile (DigiTPro) is part of TrainerLinked.in</p>
          <p className="mt-1">© 2025 TrainerLinked.in - Professional Trainer Network</p>
        </div>
      </div>
    </div>
  );
};

export default DigiTPro;
